<template>
  <div class="pending-details-page">
    <div class="container message-wrapper py-4 py-lg-5">
      <button class="back-button" @click="$router.go(-1)">
        <i class="fas fa-arrow-left mr-2" aria-hidden="true"></i>
        <span>Back to NGO Management</span>
      </button>

      <div v-if="organization" class="details-shell">
        <section class="details-hero">
          <div class="hero-copy">
            <p class="hero-eyebrow">Pending Organization Review</p>
            <h1>{{ organization.name }}</h1>
            <p class="hero-description">
              Review the organization profile, verify supporting details, and decide whether this
              registration should move forward or be returned with feedback.
            </p>

            <div class="hero-meta">
              <span
                class="status-pill"
                :class="{
                  'status-pill--approved': organization.status === 'Approved',
                  'status-pill--pending': organization.status === 'Pending',
                  'status-pill--rejected': organization.status === 'Rejected',
                }"
              >
                {{ displayValue(organization.status) }}
              </span>
              <span class="meta-pill">
                {{ organization.state ? organization.state : 'No state' }}
              </span>
              <span class="meta-pill">
                {{ organization.geo_zone ? organization.geo_zone : 'No geo zone' }}
              </span>
            </div>
          </div>

          <div class="hero-stats">
            <div class="hero-stat">
              <span class="hero-stat__label">Contacts</span>
              <strong class="hero-stat__value">{{
                organization.contact_persons ? organization.contact_persons.length : 0
              }}</strong>
            </div>
            <div class="hero-stat">
              <span class="hero-stat__label">Focus Areas</span>
              <strong class="hero-stat__value">{{
                organization.areas_of_focus ? organization.areas_of_focus.length : 0
              }}</strong>
            </div>
            <div class="hero-stat">
              <span class="hero-stat__label">Active</span>
              <strong class="hero-stat__value">{{
                organization.active === null || organization.active === undefined
                  ? 'N/A'
                  : organization.active
                  ? 'Yes'
                  : 'No'
              }}</strong>
            </div>
          </div>
        </section>

        <section class="section-nav">
          <div class="section-nav__copy">
            <p class="section-kicker">Jump to section</p>
            <h3>Scan the record faster during review</h3>
          </div>
          <div class="section-nav__chips">
            <a
              v-for="item in sectionLinks"
              :key="item.id"
              class="section-chip"
              :href="`#section-${item.id}`"
            >
              {{ item.label }}
            </a>
          </div>
        </section>

        <div class="details-grid">
          <div class="details-main">
            <section id="section-general" class="detail-section">
              <div class="detail-section__header">
                <p class="section-kicker">General</p>
                <h2>Organization profile</h2>
              </div>
              <div class="detail-grid">
                <div class="detail-item">
                  <label>Organization Name</label>
                  <div>{{ displayValue(organization.name) }}</div>
                </div>
                <div class="detail-item">
                  <label>Tracking ID</label>
                  <div>{{ displayValue(organization.tracking_id) }}</div>
                </div>
                <div class="detail-item">
                  <label>Type</label>
                  <div>{{ displayValue(organization.type) }}</div>
                </div>
                <div class="detail-item">
                  <label>Establishment Year</label>
                  <div>{{ displayValue(organization.establishment_year) }}</div>
                </div>
                <div class="detail-item">
                  <label>File No</label>
                  <div>{{ displayValue(organization.file_no) }}</div>
                </div>
                <div class="detail-item">
                  <label>Email</label>
                  <div>{{ displayValue(organization.email) }}</div>
                </div>
                <div class="detail-item">
                  <label>Phone Number</label>
                  <div>{{ displayValue(organization.phone_number) }}</div>
                </div>
                <div class="detail-item detail-item--wide">
                  <label>Address</label>
                  <div>{{ displayValue(organization.address) }}</div>
                </div>
                <div class="detail-item">
                  <label>State</label>
                  <div>{{ displayValue(organization.state) }}</div>
                </div>
                <div class="detail-item">
                  <label>Geo Zone</label>
                  <div>{{ displayValue(organization.geo_zone) }}</div>
                </div>
                <div class="detail-item detail-item--wide">
                  <label>Branch</label>
                  <div
                    v-if="getListValues(organization.branch || organization.branch_states).length"
                    class="tag-wrap"
                  >
                    <span
                      v-for="(branchState, index) in getListValues(
                        organization.branch || organization.branch_states
                      )"
                      :key="`${branchState}-${index}`"
                      class="tags"
                    >
                      {{ branchState }}
                    </span>
                  </div>
                  <div v-else>Not Provided</div>
                </div>
                <div class="detail-item">
                  <label>Web URL</label>
                  <div>{{ displayValue(organization.web_url) }}</div>
                </div>
                <div class="detail-item">
                  <label>Organization Focus</label>
                  <div>{{ displayValue(organization.organization_focus) }}</div>
                </div>
                <div class="detail-item detail-item--wide">
                  <label>Description</label>
                  <div>{{ displayValue(organization.description) }}</div>
                </div>
              </div>
            </section>

            <section id="section-account" class="detail-section">
              <div class="detail-section__header">
                <p class="section-kicker">Account</p>
                <h2>User account</h2>
              </div>
              <div class="detail-grid">
                <div class="detail-item">
                  <label>User ID</label>
                  <div>{{ displayValue(organization.user && organization.user.id) }}</div>
                </div>
                <div class="detail-item">
                  <label>Username</label>
                  <div>{{ displayValue(organization.user && organization.user.username) }}</div>
                </div>
                <div class="detail-item">
                  <label>User Email</label>
                  <div>{{ displayValue(organization.user && organization.user.email) }}</div>
                </div>
              </div>
            </section>

            <section id="section-ownership" class="detail-section">
              <div class="detail-section__header">
                <p class="section-kicker">Ownership</p>
                <h2>Umbrella and coverage structure</h2>
              </div>
              <div class="detail-grid">
                <div class="detail-item detail-item--wide">
                  <label>Umbrella Organization</label>
                  <div v-if="getListValues(organization.umbrella_org).length" class="tag-wrap">
                    <span
                      v-for="(umbrellaOrganization, index) in getListValues(
                        organization.umbrella_org
                      )"
                      :key="`${umbrellaOrganization}-${index}`"
                      class="tags"
                    >
                      {{ umbrellaOrganization }}
                    </span>
                  </div>
                  <div v-else>{{ displayValue(null) }}</div>
                </div>
                <div class="detail-item">
                  <label>Is Umbrella Organization</label>
                  <div>
                    {{
                      organization.is_umbrella_organization === null ||
                      organization.is_umbrella_organization === undefined
                        ? 'Not Provided'
                        : organization.is_umbrella_organization
                        ? 'Yes'
                        : 'No'
                    }}
                  </div>
                </div>
                <div class="detail-item">
                  <label>Scope</label>
                  <div>{{ displayValue(organization.scope) }}</div>
                </div>
                <div class="detail-item">
                  <label>Scope of Coverage</label>
                  <div>{{ displayValue(organization.scope_of_coverage) }}</div>
                </div>
              </div>
            </section>

            <section id="section-renewals" class="detail-section">
              <div class="detail-section__header">
                <p class="section-kicker">Registration history</p>
                <h2>Years Renewed</h2>
              </div>
              <div
                v-if="renewalYears.length"
                class="tag-wrap"
                aria-label="Renewal years, newest first"
              >
                <span v-for="year in renewalYears" :key="year" class="tags">{{ year }}</span>
              </div>
              <p v-else class="mb-0">No renewal history available.</p>
            </section>

            <section id="section-contacts" class="detail-section">
              <div class="detail-section__header">
                <p class="section-kicker">Contacts</p>
                <h2>Contact persons</h2>
              </div>
              <div
                v-if="organization.contact_persons && organization.contact_persons.length"
                class="subcard-grid"
              >
                <article
                  v-for="person in organization.contact_persons"
                  :key="person.id"
                  class="mini-card"
                >
                  <div class="mini-card__header">
                    <div>
                      <p class="mini-card__eyebrow">Contact Person</p>
                      <h3>{{ displayValue(person.full_name) }}</h3>
                    </div>
                    <span class="mini-card__role">{{ displayValue(person.position) }}</span>
                  </div>

                  <div class="mini-card__body">
                    <div class="mini-card__item">
                      <span>Email</span>
                      <p>{{ displayValue(person.email) }}</p>
                    </div>
                    <div class="mini-card__item">
                      <span>Phone</span>
                      <p>{{ displayValue(person.phone_number) }}</p>
                    </div>
                    <div class="mini-card__item mini-card__item--wide">
                      <span>Address</span>
                      <p>{{ displayValue(person.address) }}</p>
                    </div>
                  </div>

                  <div class="mini-card__footer">
                    <span class="mini-card__footer-label">CV File</span>
                    <template v-if="person.cv_url">
                      <a class="mini-card__link" :href="person.cv_url" target="_blank">
                        {{ getResourceLabel(person.cv_url, 'cv') }}
                      </a>
                    </template>
                    <template v-else>
                      <span class="mini-card__muted">Not Provided</span>
                    </template>
                  </div>
                </article>
              </div>
              <p v-else class="empty-copy">Not Provided</p>
            </section>

            <section id="section-focus" class="detail-section">
              <div class="detail-section__header">
                <p class="section-kicker">Focus</p>
                <h2>Areas of focus</h2>
              </div>
              <div
                v-if="organization.areas_of_focus && organization.areas_of_focus.length"
                class="tag-wrap"
              >
                <span v-for="item in organization.areas_of_focus" :key="item.id" class="tags">
                  {{ item.name }}
                </span>
              </div>
              <p v-else class="empty-copy">Not Provided</p>
            </section>

            <section id="section-banking" class="detail-section">
              <div class="detail-section__header">
                <p class="section-kicker">Funding</p>
                <h2>Banking and funding</h2>
              </div>
              <div class="list-grouping">
                <div class="list-block">
                  <h3>Bankers</h3>
                  <ul v-if="organization.bankers && organization.bankers.length">
                    <li v-for="bank in organization.bankers" :key="bank.id">
                      <b>{{ displayValue(bank.name) }}</b> — {{ displayValue(bank.address) }}
                    </li>
                  </ul>
                  <p v-else class="empty-copy">Not Provided</p>
                </div>

                <div class="list-block">
                  <h3>Sources of Funding</h3>
                  <ul
                    v-if="organization.sources_of_funding && organization.sources_of_funding.length"
                  >
                    <li v-for="fund in organization.sources_of_funding" :key="fund.id">
                      {{ displayValue(fund.source_name) }} — {{ displayValue(fund.description) }}
                    </li>
                  </ul>
                  <p v-else class="empty-copy">Not Provided</p>
                </div>

                <div class="list-block">
                  <h3>Funding List (Other)</h3>
                  <p>{{ displayValue(organization.funding_list) }}</p>
                </div>
              </div>
            </section>

            <section id="section-projects" class="detail-section">
              <div class="detail-section__header">
                <p class="section-kicker">Projects</p>
                <h2>Past projects and outputs</h2>
              </div>
              <div class="list-grouping">
                <div class="list-block">
                  <h3>Past Projects</h3>
                  <ul v-if="organization.past_projects && organization.past_projects.length">
                    <li v-for="proj in organization.past_projects" :key="proj.id">
                      <b>{{ displayValue(proj.title) }}</b> — Funded by
                      {{ displayValue(proj.funder) }} ({{ displayValue(proj.duration) }}) —
                      {{ formatCurrencyValue(proj.amount) }}
                    </li>
                  </ul>
                  <p v-else class="empty-copy">Not Provided</p>
                </div>

                <div class="list-block">
                  <h3>Publications</h3>
                  <ul v-if="organization.publications && organization.publications.length">
                    <li v-for="pub in organization.publications" :key="pub.id">
                      {{ displayValue(pub.name) }} —
                      <span v-if="pub.sample_url">
                        <a :href="pub.sample_url" target="_blank">
                          {{ getResourceLabel(pub.sample_url, 'sample') }}
                        </a>
                      </span>
                      <span v-else>Sample: Not Provided</span>
                    </li>
                  </ul>
                  <p v-else class="empty-copy">Not Provided</p>
                </div>

                <div class="list-block">
                  <h3>Resource Persons</h3>
                  <ul v-if="organization.resource_persons && organization.resource_persons.length">
                    <li v-for="rp in organization.resource_persons" :key="rp.id">
                      {{ displayValue(rp.name) }} —
                      <span v-if="rp.cv_url">
                        <a :href="rp.cv_url" target="_blank">
                          {{ getResourceLabel(rp.cv_url, 'cv') }}
                        </a>
                      </span>
                      <span v-else>CV: Not Provided</span>
                    </li>
                  </ul>
                  <p v-else class="empty-copy">Not Provided</p>
                </div>

                <div class="list-block">
                  <h3>Physical Facilities</h3>
                  <ul
                    v-if="
                      organization.physical_facilities && organization.physical_facilities.length
                    "
                  >
                    <li v-for="fac in organization.physical_facilities" :key="fac.facility_name">
                      {{ displayValue(fac.facility_name) }} —
                      {{ displayValue(fac.facility_number) }}
                    </li>
                  </ul>
                  <p v-else class="empty-copy">Not Provided</p>
                </div>
              </div>
            </section>

            <section id="section-assets" class="detail-section">
              <div class="detail-section__header">
                <p class="section-kicker">Assets</p>
                <h2>Assets and resources</h2>
              </div>
              <div v-if="organization.assets && organization.assets.length" class="list-block">
                <ul>
                  <li v-for="asset in organization.assets" :key="asset.id">
                    <b>{{ displayValue(asset.name) }}</b> — {{ displayValue(asset.address) }} ({{
                      formatCurrencyValue(asset.value)
                    }})
                    <br />
                    <small>{{ displayValue(asset.description) }}</small>
                  </li>
                </ul>
              </div>
              <p v-else class="empty-copy">Not Provided</p>
            </section>

            <section id="section-collaborations" class="detail-section">
              <div class="detail-section__header">
                <p class="section-kicker">Collaborations</p>
                <h2>Partnerships</h2>
              </div>
              <div class="detail-grid">
                <div class="detail-item">
                  <label>Is Collaborative</label>
                  <div>
                    {{
                      organization.is_collaborative === null ||
                      organization.is_collaborative === undefined
                        ? 'Not Provided'
                        : organization.is_collaborative
                        ? 'Yes'
                        : 'No'
                    }}
                  </div>
                </div>
                <div class="detail-item detail-item--wide">
                  <label>Collaboration Partners</label>
                  <div
                    v-if="organization.collaboration_list && organization.collaboration_list.length"
                  >
                    <ul class="compact-list">
                      <li v-for="(partner, index) in organization.collaboration_list" :key="index">
                        {{ displayValue(partner) }}
                      </li>
                    </ul>
                  </div>
                  <div v-else>Not Provided</div>
                </div>
              </div>
            </section>

            <section id="section-staff" class="detail-section">
              <div class="detail-section__header">
                <p class="section-kicker">Staff</p>
                <h2>Staff breakdown</h2>
              </div>
              <div v-if="organization.staff_member" class="staff-grid">
                <div class="staff-card staff-card--summary">
                  <p class="staff-card__eyebrow">Workforce Overview</p>
                  <div class="staff-card__summary">
                    <div>
                      <h3>Total Staff</h3>
                      <p class="staff-card__copy">
                        Snapshot of the organization's reported workforce capacity.
                      </p>
                    </div>
                    <strong class="staff-card__headline">
                      {{ displayValue(organization.staff_member.number_of_staffs) }}
                    </strong>
                  </div>
                </div>
                <div class="staff-card">
                  <div class="staff-card__header">
                    <div>
                      <p class="staff-card__eyebrow">Leadership Layer</p>
                      <h3>Senior Staff</h3>
                    </div>
                    <span class="staff-card__badge">
                      {{ displayValue(organization.staff_member.number_of_senior_staffs) }}
                    </span>
                  </div>
                  <div class="staff-card__metrics">
                    <article class="staff-metric">
                      <span class="staff-metric__label">Total</span>
                      <strong class="staff-metric__value">{{
                        displayValue(organization.staff_member.number_of_senior_staffs)
                      }}</strong>
                    </article>
                    <article class="staff-metric">
                      <span class="staff-metric__label">Male</span>
                      <strong class="staff-metric__value">{{
                        displayValue(organization.staff_member.number_of_male_senior_staffs)
                      }}</strong>
                    </article>
                    <article class="staff-metric">
                      <span class="staff-metric__label">Female</span>
                      <strong class="staff-metric__value">{{
                        displayValue(organization.staff_member.number_of_female_senior_staffs)
                      }}</strong>
                    </article>
                  </div>
                </div>
                <div class="staff-card">
                  <div class="staff-card__header">
                    <div>
                      <p class="staff-card__eyebrow">Operational Layer</p>
                      <h3>Junior Staff</h3>
                    </div>
                    <span class="staff-card__badge">
                      {{ displayValue(organization.staff_member.number_of_junior_staffs) }}
                    </span>
                  </div>
                  <div class="staff-card__metrics">
                    <article class="staff-metric">
                      <span class="staff-metric__label">Total</span>
                      <strong class="staff-metric__value">{{
                        displayValue(organization.staff_member.number_of_junior_staffs)
                      }}</strong>
                    </article>
                    <article class="staff-metric">
                      <span class="staff-metric__label">Male</span>
                      <strong class="staff-metric__value">{{
                        displayValue(organization.staff_member.number_of_male_junior_staffs)
                      }}</strong>
                    </article>
                    <article class="staff-metric">
                      <span class="staff-metric__label">Female</span>
                      <strong class="staff-metric__value">{{
                        displayValue(organization.staff_member.number_of_female_junior_staffs)
                      }}</strong>
                    </article>
                  </div>
                </div>
                <div class="staff-card">
                  <div class="staff-card__header">
                    <div>
                      <p class="staff-card__eyebrow">Engagement Mix</p>
                      <h3>Employment Type</h3>
                    </div>
                  </div>
                  <div class="staff-card__metrics staff-card__metrics--dual">
                    <article class="staff-metric">
                      <span class="staff-metric__label">Full-Time</span>
                      <strong class="staff-metric__value">{{
                        displayValue(organization.staff_member.number_of_full_time_staffs)
                      }}</strong>
                    </article>
                    <article class="staff-metric">
                      <span class="staff-metric__label">Part-Time</span>
                      <strong class="staff-metric__value">{{
                        displayValue(organization.staff_member.number_of_part_time_staffs)
                      }}</strong>
                    </article>
                  </div>
                </div>
              </div>
              <p v-else class="empty-copy">Not Provided</p>
            </section>

            <section id="section-files" class="detail-section">
              <div class="detail-section__header">
                <p class="section-kicker">Files</p>
                <h2>Files and documentation</h2>
              </div>
              <div class="detail-grid">
                <div class="detail-item">
                  <label>Umbrella Org File (URL)</label>
                  <div>
                    <a
                      v-if="organization.umbrella_org_file_url"
                      :href="organization.umbrella_org_file_url"
                      target="_blank"
                      download
                    >
                      Download
                    </a>
                    <span v-else>Not Provided</span>
                  </div>
                </div>
                <div class="detail-item">
                  <label>Activities Documentation (URL)</label>
                  <div>
                    <a
                      v-if="organization.activities_documentation_file_url"
                      :href="organization.activities_documentation_file_url"
                      target="_blank"
                      download
                    >
                      Download
                    </a>
                    <span v-else>Not Provided</span>
                  </div>
                </div>
                <div class="detail-item">
                  <label>Letter of Introduction (URL)</label>
                  <div>
                    <a
                      v-if="organization.letter_of_introduction_url"
                      :href="organization.letter_of_introduction_url"
                      target="_blank"
                      download
                    >
                      Download
                    </a>
                    <span v-else>Not Provided</span>
                  </div>
                </div>
                <div class="detail-item">
                  <label>File Logo (URL)</label>
                  <div>
                    <a
                      v-if="organization.file_logo_url"
                      :href="organization.file_logo_url"
                      target="_blank"
                      download
                    >
                      Download
                    </a>
                    <span v-else>Not Provided</span>
                  </div>
                </div>
                <div class="detail-item">
                  <label>Umbrella Org File (Raw)</label>
                  <div>{{ displayValue(organization.umbrella_org_file) }}</div>
                </div>
                <div class="detail-item">
                  <label>Activities Documentation File (Raw)</label>
                  <div>{{ displayValue(organization.activities_documentation_file) }}</div>
                </div>
                <div class="detail-item">
                  <label>Letter of Introduction (Raw)</label>
                  <div>{{ displayValue(organization.letter_of_introduction) }}</div>
                </div>
                <div class="detail-item">
                  <label>File Logo (Raw)</label>
                  <div>{{ displayValue(organization.file_logo) }}</div>
                </div>
              </div>
            </section>

            <section id="section-meta" class="detail-section">
              <div class="detail-section__header">
                <p class="section-kicker">Meta</p>
                <h2>Meta information</h2>
              </div>
              <div class="detail-grid">
                <div class="detail-item">
                  <label>Organization ID</label>
                  <div>{{ displayValue(organization.id) }}</div>
                </div>
                <div class="detail-item">
                  <label>Registered On</label>
                  <div>{{ formatDateValue(organization.registered_on) }}</div>
                </div>
                <div class="detail-item">
                  <label>Last Updated</label>
                  <div>{{ formatDateValue(organization.updated_on) }}</div>
                </div>
                <div
                  v-if="organization.status === 'Rejected'"
                  class="detail-item detail-item--wide"
                >
                  <label>Reject Reason</label>
                  <div>{{ displayValue(organization.reject_reason) }}</div>
                </div>
                <div
                  v-if="organization.pending_reason_message"
                  class="detail-item detail-item--wide detail-item--alert"
                >
                  <label>Pending Reason Message</label>
                  <div class="detail-item__alert-value">
                    {{ displayValue(organization.pending_reason_message) }}
                  </div>
                </div>
              </div>
            </section>

            <section id="section-reports" class="detail-section">
              <div class="detail-section__header">
                <p class="section-kicker">Reports</p>
                <h2>Reports</h2>
              </div>
              <div v-if="organization.reports && organization.reports.length">
                <div
                  v-for="(report, index) in organization.reports"
                  :key="index"
                  class="report-card"
                >
                  <pre class="mb-0">{{ formatJSON(report) }}</pre>
                </div>
              </div>
              <p v-else class="empty-copy">Not Provided</p>
            </section>
          </div>

          <aside class="review-panel">
            <section id="section-review" class="review-card">
              <p class="section-kicker">Decision</p>
              <h2>Approval actions</h2>
              <p class="review-copy">
                Approve this organization if the submission is complete, or send it back with a
                clear rejection reason.
              </p>

              <div class="review-status">
                <span class="review-status__label">Current status</span>
                <strong>{{ displayValue(organization.status) }}</strong>
              </div>

              <div class="review-status">
                <span class="review-status__label">Account active</span>
                <strong>
                  {{
                    organization.active === null || organization.active === undefined
                      ? 'Not Provided'
                      : organization.active
                      ? 'Yes'
                      : 'No'
                  }}
                </strong>
              </div>

              <div
                v-if="organization.pending_reason_message"
                class="review-status review-status--stack"
              >
                <span class="review-status__label">Pending reason</span>
                <strong>{{ displayValue(organization.pending_reason_message) }}</strong>
              </div>

              <button
                v-if="
                  organization.status !== 'Approved' && !showApproveConfirmation && !showReasonBox
                "
                class="review-button review-button--approve"
                :disabled="isSubmitting"
                @click="toggleApproveConfirmation"
              >
                Approve Organization
              </button>

              <div
                v-if="showApproveConfirmation && organization.status !== 'Approved'"
                class="approval-confirmation"
              >
                <p class="approval-confirmation__copy">
                  Approving this organization will activate it for the next stage. Do you want to
                  proceed?
                </p>

                <div class="approval-confirmation__actions">
                  <button
                    class="review-button review-button--approve review-button--inline"
                    :disabled="isSubmitting"
                    @click="approve"
                  >
                    {{ isSubmitting ? 'Approving...' : 'Proceed' }}
                  </button>
                  <button
                    class="review-button review-button--ghost review-button--inline"
                    :disabled="isSubmitting"
                    @click="cancelApproveConfirmation"
                  >
                    Cancel
                  </button>
                </div>
              </div>

              <div v-if="showReasonBox" class="reason-text">
                <label>Reason for rejection</label>
                <textarea
                  v-model="reason"
                  placeholder="Provide a clear reason for rejecting this submission"
                ></textarea>

                <button
                  class="review-button review-button--ghost review-button--after-reason"
                  :disabled="isSubmitting"
                  @click="cancelReject"
                >
                  Cancel Rejection
                </button>
              </div>

              <button
                class="review-button review-button--reject"
                :class="{ 'review-button--after-reason': showReasonBox }"
                :disabled="isSubmitting"
                @click="reject"
              >
                {{ showReasonBox ? 'Send Reason & Reject ' : 'Reject Organization' }}
              </button>
            </section>
          </aside>
        </div>
      </div>

      <div v-else class="loading-shell">
        <div class="spinner-border load-icon" role="status"></div>
        <p>Loading organization details...</p>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getOrganization,
  approveOrganization,
  rejectOrganization,
} from '../../../../ngo/config/api-modules/api.organization';

export default {
  data() {
    return {
      organization: null,
      isLoading: false,
      isSubmitting: false,
      showApproveConfirmation: false,
      showReasonBox: false,
      reason: '',
    };
  },

  computed: {
    renewalYears() {
      const years = this.organization?.years_renewed;
      if (!Array.isArray(years)) return [];
      const validYears = years
        .map((year) => String(year ?? '').trim())
        .filter((year) => /^\d{4}$/.test(year));
      return [...new Set(validYears)].sort((a, b) => Number(b) - Number(a));
    },
    sectionLinks() {
      return [
        { id: 'general', label: 'General' },
        { id: 'account', label: 'Account' },
        { id: 'ownership', label: 'Ownership' },
        { id: 'renewals', label: 'Years Renewed' },
        { id: 'contacts', label: 'Contacts' },
        { id: 'focus', label: 'Focus' },
        { id: 'banking', label: 'Funding' },
        { id: 'projects', label: 'Projects' },
        { id: 'assets', label: 'Assets' },
        { id: 'collaborations', label: 'Collaborations' },
        { id: 'staff', label: 'Staff' },
        { id: 'files', label: 'Files' },
        { id: 'meta', label: 'Meta' },
        { id: 'reports', label: 'Reports' },
      ];
    },
  },

  methods: {
    hasContent(value) {
      return value !== null && value !== undefined && String(value).trim() !== '';
    },
    displayValue(value) {
      if (value === null || value === undefined || value === '') {
        return 'Not Provided';
      }
      return value;
    },
    getListValues(value) {
      if (value === null || value === undefined || value === '') {
        return [];
      }

      let normalizedValue = value;

      if (typeof normalizedValue === 'string') {
        const trimmedValue = normalizedValue.trim();

        if (!trimmedValue) {
          return [];
        }

        try {
          const parsedValue = JSON.parse(trimmedValue);
          normalizedValue = parsedValue;
        } catch (error) {
          return [trimmedValue];
        }
      }

      if (Array.isArray(normalizedValue)) {
        return normalizedValue.map((item) => String(item || '').trim()).filter(Boolean);
      }

      return [String(normalizedValue).trim()].filter(Boolean);
    },
    formatDateValue(value) {
      if (value === null || value === undefined || value === '') {
        return 'Not Provided';
      }

      const parsedDate = new Date(value);

      if (Number.isNaN(parsedDate.getTime())) {
        return this.displayValue(value);
      }

      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }).format(parsedDate);
    },
    formatCurrencyValue(value) {
      if (value === null || value === undefined || value === '') {
        return 'Not Provided';
      }

      const normalizedValue = typeof value === 'string' ? value.replace(/[^0-9.-]/g, '') : value;
      const amount = Number(normalizedValue);

      if (Number.isNaN(amount)) {
        return this.displayValue(value);
      }

      return new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: 'NGN',
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: 2,
      }).format(amount);
    },
    formatJSON(obj) {
      try {
        return JSON.stringify(obj, null, 2);
      } catch (e) {
        return String(obj);
      }
    },
    getResourceLabel(url, fallback = 'resource') {
      if (!url || typeof url !== 'string') {
        return 'Open Resource';
      }

      const cleanUrl = url.split('?')[0].split('#')[0];
      const extension = cleanUrl.includes('.') ? cleanUrl.split('.').pop().toLowerCase() : '';

      if (extension === 'pdf') {
        return 'View PDF';
      }

      if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) {
        return 'View Image';
      }

      if (['doc', 'docx', 'odt', 'rtf'].includes(extension)) {
        return 'Open Document';
      }

      if (['xls', 'xlsx', 'csv'].includes(extension)) {
        return 'Open Spreadsheet';
      }

      if (['ppt', 'pptx'].includes(extension)) {
        return 'Open Presentation';
      }

      if (fallback === 'cv') {
        return 'View CV';
      }

      if (fallback === 'sample') {
        return 'View Sample';
      }

      return 'Open Resource';
    },

    async getOrganizationByID() {
      this.isLoading = true;
      try {
        const { id } = this.$route.params;
        const response = await getOrganization(id);
        this.organization = response.data.data;
      } catch (error) {
        this.organization = null;
      } finally {
        this.isLoading = false;
      }
    },

    toggleApproveConfirmation() {
      const missingFields = this.getMissingApprovalFields();

      if (missingFields.length) {
        this.$swal({
          title: 'Missing required fields',
          text: `Add ${missingFields.join(', ')} before approving this organization.`,
          icon: 'error',
        });
        return;
      }

      this.showApproveConfirmation = !this.showApproveConfirmation;

      if (this.showApproveConfirmation) {
        this.showReasonBox = false;
      }
    },

    cancelApproveConfirmation() {
      this.showApproveConfirmation = false;
    },

    cancelReject() {
      this.showReasonBox = false;
      this.reason = '';
    },

    getMissingApprovalFields() {
      const requiredFields = [
        { label: 'organization name', value: this.organization && this.organization.name },
        {
          label: 'email',
          value:
            this.organization?.email ||
            this.organization?.contact_email ||
            this.organization?.user?.email,
        },
      ];

      return requiredFields
        .filter((field) => !this.hasContent(field.value))
        .map((field) => field.label);
    },

    async approve() {
      const missingFields = this.getMissingApprovalFields();

      if (missingFields.length) {
        this.showApproveConfirmation = false;
        this.$swal({
          title: 'Missing required fields',
          text: `Add ${missingFields.join(', ')} before approving this organization.`,
          icon: 'error',
        });
        return;
      }

      this.isSubmitting = true;
      try {
        const { id } = this.$route.params;
        await approveOrganization(id);
        this.$swal({
          title: 'Success',
          text: 'Organization Approved Successfully',
          icon: 'success',
        });
        this.showApproveConfirmation = false;
        this.organization.status = 'Approved';
        await this.getOrganizationByID();
        this.$router.push({ path: '/admin/organization', query: { tab: 'Approved' } });
      } catch {
        this.$swal({
          title: 'Error',
          text: 'Error Approving Organization',
          icon: 'error',
        });
      } finally {
        this.isSubmitting = false;
      }
    },

    async reject() {
      this.showApproveConfirmation = false;

      if (!this.showReasonBox) {
        this.showReasonBox = true;
        return;
      }

      if (!this.reason) {
        this.$swal({
          title: 'Error',
          text: 'Reason is required',
          icon: 'error',
        });
        return;
      }

      this.isSubmitting = true;
      try {
        const { id } = this.$route.params;
        await rejectOrganization({ id, body: { reason: this.reason } });

        this.$swal({
          title: 'Success',
          text: 'Organization Rejected Successfully',
          icon: 'success',
        });

        this.showReasonBox = false;
        this.reason = '';
        this.getOrganizationByID();
      } catch {
        this.$swal({
          title: 'Error',
          text: 'Error Rejecting Organization',
          icon: 'error',
        });
      } finally {
        this.isSubmitting = false;
      }
    },
  },

  mounted() {
    this.getOrganizationByID();
  },
};
</script>

<style lang="scss" scoped>
.pending-details-page {
  min-height: 100vh;
  padding: 1rem 0 2.5rem;
  background: radial-gradient(circle at top left, rgba(48, 171, 76, 0.14), transparent 24%),
    linear-gradient(180deg, #f5faf6 0%, #eef5ef 100%);
}

.message-wrapper {
  max-width: 1360px;
}

.back-button {
  display: inline-flex;
  align-items: center;
  padding: 0.85rem 1.15rem;
  margin-bottom: 1.25rem;
  border: 0;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.88);
  box-shadow: 0 14px 32px rgba(18, 43, 25, 0.08);
  color: #1f3826;
  font-weight: 700;
  width: fit-content;
}

.details-shell {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.details-hero {
  display: flex;
  justify-content: space-between;
  gap: 1.5rem;
  padding: 2rem;
  border: 1px solid rgba(24, 57, 31, 0.08);
  border-radius: 30px;
  background: linear-gradient(135deg, rgba(10, 33, 17, 0.96), rgba(23, 72, 36, 0.88)),
    linear-gradient(140deg, #16391f 0%, #30ab4c 100%);
  box-shadow: 0 28px 80px rgba(17, 41, 24, 0.16);
  color: #f7fff7;
}

.hero-copy {
  max-width: 48rem;
}

.hero-eyebrow,
.section-kicker {
  margin-bottom: 0.5rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.hero-eyebrow {
  color: rgba(247, 255, 247, 0.72);
}

.details-hero h1 {
  margin-bottom: 0.85rem;
  font-family: 'Open Sans', sans-serif;
  font-size: clamp(2rem, 3vw, 3rem);
  line-height: 1.08;
}

.hero-description {
  margin-bottom: 1.2rem;
  max-width: 42rem;
  color: rgba(247, 255, 247, 0.82);
  line-height: 1.75;
}

.hero-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
}

.status-pill,
.meta-pill {
  display: inline-flex;
  align-items: center;
  min-height: 2.3rem;
  padding: 0.4rem 0.9rem;
  border-radius: 999px;
}

.status-pill {
  font-weight: 700;
}

.status-pill--approved {
  background: rgba(232, 245, 235, 0.18);
  color: #d9ffe1;
}

.status-pill--pending {
  background: rgba(255, 243, 205, 0.16);
  color: #fff1bb;
}

.status-pill--rejected {
  background: rgba(255, 231, 231, 0.16);
  color: #ffd1d1;
}

.meta-pill {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(247, 255, 247, 0.82);
  font-size: 0.9rem;
}

.hero-stats {
  display: grid;
  grid-template-columns: repeat(1, minmax(180px, 1fr));
  gap: 1rem;
  min-width: 16rem;
}

.hero-stat {
  padding: 1rem 1.1rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
}

.hero-stat__label {
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.8rem;
  color: rgba(247, 255, 247, 0.72);
}

.hero-stat__value {
  font-size: 1.35rem;
  line-height: 1.3;
}

.section-nav {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.25rem;
  padding: 1.2rem 1.35rem;
  border: 1px solid rgba(24, 57, 31, 0.08);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.72);
  box-shadow: 0 14px 40px rgba(22, 50, 28, 0.06);
}

.section-kicker {
  color: #2c8d41;
}

.section-nav__copy h3 {
  margin: 0;
  font-family: 'Open Sans', sans-serif;
  font-size: 1.15rem;
  color: #14261a;
}

.section-nav__chips {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.6rem;
}

.section-chip {
  display: inline-flex;
  align-items: center;
  min-height: 2.4rem;
  padding: 0.5rem 0.95rem;
  border-radius: 999px;
  background: #edf7ef;
  color: #256536;
  font-size: 0.84rem;
  font-weight: 700;
  text-decoration: none;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.section-chip:hover {
  transform: translateY(-1px);
  background: #e3f2e7;
  box-shadow: 0 10px 20px rgba(48, 171, 76, 0.12);
}

.details-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.7fr) minmax(300px, 0.7fr);
  gap: 1.5rem;
  align-items: start;
}

.details-main {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.detail-section,
.review-card {
  padding: 1.4rem;
  border: 1px solid rgba(28, 68, 38, 0.08);
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(251, 253, 251, 0.96), rgba(241, 247, 242, 0.96));
  box-shadow: 0 10px 28px rgba(18, 42, 24, 0.05);
  scroll-margin-top: 24px;
}

.detail-section__header {
  margin-bottom: 1rem;
}

.detail-section__header h2,
.review-card h2 {
  margin: 0;
  font-family: 'Open Sans', sans-serif;
  color: #14261a;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.detail-item--wide {
  grid-column: 1 / -1;
}

.detail-item--alert label {
  color: #bb4e4e;
}

.detail-item label,
.mini-card__body span,
.staff-card span,
.review-status__label {
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #68786f;
}

.detail-item div {
  padding: 0.95rem 1rem;
  border: 1px solid rgba(28, 68, 38, 0.08);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.8);
  color: #213228;
  line-height: 1.65;
  word-break: break-word;
}

.detail-item .detail-item__alert-value {
  border-color: rgba(187, 78, 78, 0.22);
  background: rgba(255, 240, 240, 0.92);
  color: #bb4e4e;
}

.subcard-grid,
.staff-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.mini-card,
.staff-card,
.report-card,
.list-block {
  padding: 1rem;
  border: 1px solid rgba(28, 68, 38, 0.08);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.78);
}

.mini-card h3,
.staff-card h3,
.list-block h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #1c3022;
}

.mini-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.mini-card__eyebrow {
  margin: 0 0 0.3rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #6d7d74;
}

.mini-card__role {
  display: inline-flex;
  align-items: center;
  padding: 0.42rem 0.75rem;
  border-radius: 999px;
  background: #edf7ef;
  color: #256536;
  font-size: 0.78rem;
  font-weight: 700;
  text-align: center;
}

.mini-card__body {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
}

.mini-card__item {
  padding: 0.9rem 1rem;
  border: 1px solid rgba(28, 68, 38, 0.08);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.82);
}

.mini-card__item--wide {
  grid-column: 1 / -1;
}

.mini-card__item p,
.staff-card {
  margin: 0;
}

.mini-card__item p {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  color: #213228;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.staff-card {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: radial-gradient(circle at top right, rgba(48, 171, 76, 0.14), transparent 32%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(245, 251, 246, 0.92));
  box-shadow: 0 18px 36px rgba(17, 41, 24, 0.06);
}

.staff-card--summary {
  grid-column: 1 / -1;
  padding: 1.35rem;
  border-color: rgba(48, 171, 76, 0.18);
  background: radial-gradient(circle at top right, rgba(99, 214, 130, 0.22), transparent 30%),
    linear-gradient(135deg, #173a20 0%, #22562f 56%, #2f8a45 100%);
  color: #f5fff7;
}

.staff-card--summary h3 {
  color: #ffffff;
}

.staff-card__summary {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
}

.staff-card__copy {
  margin: 0.45rem 0 0;
  max-width: 30rem;
  color: rgba(245, 255, 247, 0.78);
  line-height: 1.6;
}

.staff-card__headline {
  font-size: clamp(2.1rem, 4vw, 3.6rem);
  line-height: 1;
  letter-spacing: -0.04em;
  color: #ffffff;
}

.staff-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.staff-card__eyebrow {
  margin: 0 0 0.35rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #6d7d74;
}

.staff-card--summary .staff-card__eyebrow {
  color: rgba(245, 255, 247, 0.74);
}

.staff-card__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 3rem;
  min-height: 3rem;
  padding: 0.55rem 0.9rem;
  border-radius: 18px;
  background: linear-gradient(180deg, #edf7ef, #dff1e4);
  color: #1f5e31;
  font-size: 1.2rem;
  font-weight: 800;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9);
}

.staff-card__metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.85rem;
}

.staff-card__metrics--dual {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.staff-metric {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 0.45rem;
  min-height: 6.2rem;
  padding: 0.95rem 1rem;
  border: 1px solid rgba(28, 68, 38, 0.08);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.86);
}

.staff-metric__label {
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #718178;
}

.staff-metric__value {
  font-size: 1.7rem;
  line-height: 1;
  letter-spacing: -0.03em;
  color: #173a20;
}

.mini-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(28, 68, 38, 0.08);
}

.mini-card__footer-label {
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #68786f;
}

.mini-card__link {
  display: inline-flex;
  align-items: center;
  padding: 0.55rem 0.9rem;
  border-radius: 999px;
  background: #edf7ef;
  color: #256536;
  font-weight: 700;
  text-decoration: none;
}

.mini-card__muted {
  color: #7a867f;
}

.tag-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
}

.tags {
  display: inline-flex;
  align-items: center;
  padding: 0.55rem 0.9rem;
  border-radius: 999px;
  background: #edf7ef;
  color: #256536;
  font-weight: 600;
}

.list-grouping {
  display: grid;
  gap: 1rem;
}

.list-block ul,
.compact-list {
  margin: 0;
  padding-left: 1.15rem;
  color: #213228;
}

.list-block li,
.compact-list li {
  margin-bottom: 0.6rem;
  line-height: 1.65;
}

.review-panel {
  position: sticky;
  top: 1rem;
}

.review-copy {
  margin-bottom: 1rem;
  color: #5d6c63;
  line-height: 1.7;
}

.review-status {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.9rem 1rem;
  margin-bottom: 0.9rem;
  border: 1px solid rgba(28, 68, 38, 0.08);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.78);
}

.review-status--stack {
  border-color: rgba(187, 78, 78, 0.18);
  background: rgba(255, 240, 240, 0.9);
}

.review-status--stack .review-status__label,
.review-status--stack strong {
  color: #bb4e4e;
}

.review-status--stack strong {
  line-height: 1.6;
  white-space: pre-wrap;
}

.review-button {
  width: 100%;
  min-height: 3.2rem;
  border: 0;
  border-radius: 16px;
  font-weight: 700;
  margin-top: 0.8rem;
}

.review-button:disabled {
  cursor: not-allowed;
  opacity: 0.72;
}

.review-button--approve {
  background: linear-gradient(135deg, #16391f 0%, #30ab4c 100%);
  box-shadow: 0 16px 30px rgba(48, 171, 76, 0.18);
  color: #fff;
}

.review-button--reject {
  background: #fff0f0;
  color: #bb4e4e;
}

.review-button--ghost {
  background: rgba(20, 38, 26, 0.06);
  color: #1f3826;
}

.review-button--inline {
  margin-top: 0;
}

.review-button--after-reason {
  margin-top: 1rem;
}

.approval-confirmation {
  margin-top: 0.9rem;
  padding: 1rem;
  border: 1px solid rgba(48, 171, 76, 0.16);
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(241, 250, 243, 0.96), rgba(232, 246, 236, 0.96));
}

.approval-confirmation__copy {
  margin: 0;
  color: #284232;
  line-height: 1.65;
}

.approval-confirmation__actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
}

.reason-text {
  margin-top: 1rem;
}

.reason-text label {
  display: block;
  margin-bottom: 0.45rem;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #68786f;
}

.reason-text textarea {
  width: 100%;
  min-height: 8rem;
  padding: 0.9rem 1rem;
  border: 1px solid rgba(28, 68, 38, 0.12);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7), 0 10px 20px rgba(18, 42, 24, 0.04);
}

.reason-text textarea:focus {
  outline: none;
  border-color: rgba(48, 171, 76, 0.6);
  box-shadow: 0 0 0 4px rgba(48, 171, 76, 0.1);
}

.empty-copy {
  margin: 0;
  color: #5f6f66;
}

pre {
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  padding: 1rem;
  border: 1px solid rgba(28, 68, 38, 0.08);
  border-radius: 18px;
  background: #f7faf7;
}

.loading-shell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 18rem;
  text-align: center;
}

.loading-shell p {
  margin-top: 1rem;
  color: #5f6f66;
}

.load-icon {
  height: 4rem;
  width: 4rem;
}

@media (max-width: 991px) {
  .details-hero,
  .section-nav,
  .details-grid {
    grid-template-columns: 1fr;
    flex-direction: column;
  }

  .details-hero {
    align-items: flex-start;
  }

  .hero-stats {
    width: 100%;
    min-width: 0;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .section-nav__chips {
    justify-content: flex-start;
  }

  .review-panel {
    position: static;
  }
}

@media (max-width: 767px) {
  .pending-details-page {
    padding: 0.5rem 0 2rem;
  }

  .details-hero,
  .detail-section,
  .review-card,
  .section-nav {
    padding: 1rem;
    border-radius: 22px;
  }

  .detail-grid,
  .subcard-grid,
  .staff-grid,
  .hero-stats {
    grid-template-columns: 1fr;
  }

  .mini-card__header,
  .mini-card__footer {
    flex-direction: column;
    align-items: flex-start;
  }

  .mini-card__body {
    grid-template-columns: 1fr;
  }

  .staff-card__summary,
  .staff-card__header {
    flex-direction: column;
    align-items: flex-start;
  }

  .staff-card__metrics,
  .staff-card__metrics--dual {
    grid-template-columns: 1fr;
  }

  .approval-confirmation__actions {
    grid-template-columns: 1fr;
  }
}
</style>
